// Recommendations View - Cigar recommendations list with filtering
import React, { useState } from 'react';
import { ChevronDown, Bookmark, BookMarked, Filter, ChevronLeft } from 'lucide-react';
import { CigarRecommendation, SAMPLE_RECOMMENDATIONS } from '../data/festivals';

interface RecommendationsViewProps {
  recommendations?: CigarRecommendation[];
  savedCigarIds: string[];
  onSaveCigar: (cigarId: string) => void;
  onUnsaveCigar: (cigarId: string) => void;
  onSelectCigar: (cigar: CigarRecommendation) => void;
  onBack: () => void;
}

export const RecommendationsView: React.FC<RecommendationsViewProps> = ({
  recommendations = SAMPLE_RECOMMENDATIONS,
  savedCigarIds,
  onSaveCigar,
  onUnsaveCigar,
  onSelectCigar,
  onBack,
}) => {
  const [sortBy, setSortBy] = useState<'strength' | 'name' | 'brand'>('name');
  const [filterStrength, setFilterStrength] = useState<string | null>(null);
  const [filterFlavor, setFilterFlavor] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Get unique flavors from recommendations
  const allFlavors = [...new Set(recommendations.flatMap(r => r.flavors))];

  // Filter and sort recommendations
  let filtered = [...recommendations];
  if (filterStrength) {
    filtered = filtered.filter(r => r.strength === filterStrength);
  }
  if (filterFlavor) {
    filtered = filtered.filter(r => r.flavors.includes(filterFlavor));
  }

  // Sort
  filtered.sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'brand') return a.brand.localeCompare(b.brand);
    if (sortBy === 'strength') {
      const order = ['mild', 'medium', 'medium-full', 'full'];
      return order.indexOf(a.strength) - order.indexOf(b.strength);
    }
    return 0;
  });

  const strengthColors: Record<string, string> = {
    mild: 'bg-green-500/20 text-green-400',
    medium: 'bg-amber-500/20 text-amber-400',
    'medium-full': 'bg-orange-500/20 text-orange-400',
    full: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <span className="text-white font-semibold">Recommendations</span>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <button className="text-white/50 hover:text-white">Home</button>
            <button className="text-amber-500 font-medium">My List</button>
            <button className="text-white/50 hover:text-white">Festivals</button>
          </nav>
        </div>
      </div>

      <div className="p-4">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="italic">Your Cigar</span>{' '}
            <span className="text-amber-500 italic">Recommendations</span>
          </h1>
          <p className="text-white/60">
            Based on your preferences, here are a few cigars to ask about.
          </p>
        </div>

        {/* Sort and Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showFilters ? 'bg-amber-500 text-stone-900' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <DropdownButton
            label={`Sort: ${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}`}
            options={[
              { label: 'Name', value: 'name' },
              { label: 'Brand', value: 'brand' },
              { label: 'Strength', value: 'strength' },
            ]}
            value={sortBy}
            onChange={(v) => setSortBy(v as any)}
          />
          <DropdownButton
            label={filterStrength ? `Strength: ${filterStrength}` : 'Filter by Strength'}
            options={[
              { label: 'All', value: '' },
              { label: 'Mild', value: 'mild' },
              { label: 'Medium', value: 'medium' },
              { label: 'Medium-Full', value: 'medium-full' },
              { label: 'Full', value: 'full' },
            ]}
            value={filterStrength || ''}
            onChange={(v) => setFilterStrength(v || null)}
          />
          <DropdownButton
            label={filterFlavor ? `Flavor: ${filterFlavor}` : 'Filter by Flavor'}
            options={[
              { label: 'All', value: '' },
              ...allFlavors.map(f => ({ label: f.charAt(0).toUpperCase() + f.slice(1), value: f })),
            ]}
            value={filterFlavor || ''}
            onChange={(v) => setFilterFlavor(v || null)}
          />
        </div>

        {/* Cigar grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filtered.map(cigar => (
            <CigarCard
              key={cigar.id}
              cigar={cigar}
              isSaved={savedCigarIds.includes(cigar.id)}
              onSave={() => savedCigarIds.includes(cigar.id) ? onUnsaveCigar(cigar.id) : onSaveCigar(cigar.id)}
              onClick={() => onSelectCigar(cigar)}
              strengthColors={strengthColors}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/50">No cigars match your current filters.</p>
            <button
              onClick={() => { setFilterStrength(null); setFilterFlavor(null); }}
              className="mt-2 text-amber-500 hover:text-amber-400"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Pagination dots */}
        <div className="flex justify-center gap-2 mt-8">
          {[1, 2, 3, 4].map(page => (
            <button
              key={page}
              className={`w-2 h-2 rounded-full transition-colors ${
                page === 1 ? 'bg-amber-500' : 'bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const CigarCard: React.FC<{
  cigar: CigarRecommendation;
  isSaved: boolean;
  onSave: () => void;
  onClick: () => void;
  strengthColors: Record<string, string>;
}> = ({ cigar, isSaved, onSave, onClick, strengthColors }) => (
  <div className="rounded-xl bg-stone-800 border border-white/10 overflow-hidden hover:border-white/20 transition-colors group">
    {/* Image placeholder */}
    <div className="h-40 bg-gradient-to-br from-amber-900/30 to-stone-800 relative">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-32 bg-gradient-to-b from-amber-700 to-amber-900 rounded-sm shadow-lg" />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onSave(); }}
        className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 hover:bg-black/70 transition-colors"
      >
        {isSaved ? (
          <BookMarked className="w-5 h-5 text-amber-500" />
        ) : (
          <Bookmark className="w-5 h-5 text-white/70" />
        )}
      </button>
    </div>

    {/* Content */}
    <div className="p-4" onClick={onClick}>
      <h3 className="text-white font-semibold mb-1">{cigar.name}</h3>
      <p className="text-white/50 text-sm mb-3">{cigar.brand}</p>

      <p className="text-white/70 text-sm mb-3 line-clamp-2">
        {cigar.description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${strengthColors[cigar.strength]}`}>
          {cigar.strength.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
        {cigar.flavors.slice(0, 2).map(flavor => (
          <span key={flavor} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">
            {flavor.charAt(0).toUpperCase() + flavor.slice(1)}
          </span>
        ))}
      </div>

      {/* Origin */}
      {cigar.origin && (
        <p className="text-white/40 text-xs mb-3">{cigar.origin}</p>
      )}

      <button
        onClick={onClick}
        className="w-full py-2 rounded-lg bg-amber-500 text-stone-900 font-medium text-sm hover:bg-amber-400 transition-colors"
      >
        View Details
      </button>
    </div>
  </div>
);

const DropdownButton: React.FC<{
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}> = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-48 bg-stone-800 border border-white/10 rounded-lg shadow-xl z-20 py-1">
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                  value === opt.value ? 'text-amber-500' : 'text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RecommendationsView;
